import { Link } from 'react-router-dom';
import type { ApplicationCard as Card } from '~/api/types';
import { Badge, RiskBadge, Stars, formatCount } from '~/shared/ui';

const TYPE_LABEL: Readonly<Record<string, string>> = {
  WORKER: 'Worker',
  WEB_APP: 'Web app',
  API: 'API',
  PLUGIN: 'Plugin',
  INTEGRATION: 'Integration',
  SAAS: 'SaaS',
  OTHER: 'Other',
};

/**
 * One listing in the marketplace grid.
 *
 * The risk badge sits on the card rather than only on the detail page deliberately: the decision a
 * user is making while scanning a grid is which application to look at, and permission risk is one
 * of the few things that should change that decision before they click (§25).
 */
export function ApplicationCardTile({ application }: { application: Card }): JSX.Element {
  return (
    <Link to={`/applications/${application.slug}`} className="card-hover group flex h-full flex-col">
      <div className="flex items-start gap-3">
        <Logo url={application.logoUrl} name={application.name} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-slate-100 transition-colors group-hover:text-white">
            {application.name}
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-slate-400">
            {application.developerName}
            {application.developerVerified ? (
              <span title="Verified developer" className="text-brand">
                ✓
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-400">
        {application.summary}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{TYPE_LABEL[application.type] ?? application.type}</Badge>
        <RiskBadge risk={application.permissionRisk} />
        {application.pricingModel === 'FREE' ? null : (
          <Badge tone="brand">{application.pricingLabel}</Badge>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-slate-800/80 pt-3 text-xs text-slate-500">
        <Stars average={application.ratingAverage} count={application.ratingCount} />
        <span>{formatCount(application.installCount)} installs</span>
      </div>
    </Link>
  );
}

/**
 * The logo, with initials as a fallback.
 *
 * A developer-supplied image can be missing or broken, and a broken-image icon in a grid looks like
 * the marketplace is broken rather than the listing.
 */
function Logo({ url, name }: { url: string | undefined; name: string }): JSX.Element {
  if (url === undefined) {
    return (
      <span
        aria-hidden="true"
        className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 text-sm font-bold text-slate-300 ring-1 ring-slate-700/80"
      >
        {initials(name)}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      className="h-11 w-11 shrink-0 rounded-xl bg-slate-800 object-cover ring-1 ring-slate-700/80"
    />
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

export function ApplicationGrid({
  applications,
}: {
  applications: readonly Card[];
}): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {applications.map((application) => (
        <ApplicationCardTile key={application.id} application={application} />
      ))}
    </div>
  );
}

/** A horizontally scrolling row, used for the curated rows on the landing page. */
export function ApplicationRow({
  applications,
}: {
  applications: readonly Card[];
}): JSX.Element {
  return (
    <div className="-mx-4 flex snap-x gap-4 overflow-x-auto overflow-y-hidden px-4 pb-2">
      {applications.map((application) => (
        <div key={application.id} className="w-72 shrink-0 snap-start">
          <ApplicationCardTile application={application} />
        </div>
      ))}
    </div>
  );
}
